import { selectGradeTheme, ThemeProvider, UxIcon, UxIconEnum } from "@imaginelearning/mps-ux-component-library";
import React, { Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PathwayProvider } from "context-providers/pathway.provider";
import usePageTitle from "hooks/usePageTitle";
import { OnDemandTutoringProvider } from "../ondemandtutoring";
import MyAssignmentsContainer from "../components/assignments/my-assignments-container";
import EdgeLaunch from "../components/edge-launch/edge-launch";
import EdgeSessionManager from "../components/edge-session-manager/edge-session-manager";
import { IlpProvider } from "../context-providers/ilp.provider";
import { RewardProvider } from "../context-providers/reward.provider";
import { UserProvider } from "../context-providers/user.provider";
import AppWindow from "../helpers/app-window";
import HomeContainer from "./home-container";
import IRootContainerProps from "./root-container-props";
import LinkLaunch from "../components/link-launch/link-launch";
import AssessmentContainer from "../imagineplus-assessments/containers/assessment-container";
import StudentHomeContainer from "../components/liftoff/Homepage/StudentHomeContainer";
import setFavicons from "../helpers/set-favicons";

// In future this would be set by a control on the page
// if you want to test Spanish just change this to es
// const localeProp = "en";

const spinner = <UxIcon icon={UxIconEnum.CIRCLE_NOTCH} />;
const appWindow = AppWindow() as any;
const sessionIdKey = appWindow.STUDENT_ENV.REACT_APP_SESSION_KEYNAME as string;
const logoutMessageKey = appWindow.STUDENT_ENV.REACT_APP_LOGOUT_MESSAGE_KEYNAME as string;

function MyPathRoutes(props: {
  authTokenSubject?: string;
  authTokenIsPractice?: boolean;
  authTokenIsAssignment?: boolean;
}) {
  const { authTokenSubject, authTokenIsPractice, authTokenIsAssignment } = props;
  const { updateTitle } = usePageTitle();

  useEffect(() => {
    if (updateTitle) {
      setFavicons("mypath");
      updateTitle("Imagine MyPath");
    }
  }, [updateTitle]);
  return (
    <RewardProvider>
      <IlpProvider>
        <PathwayProvider>
          <OnDemandTutoringProvider>
            <Suspense fallback={spinner}>
              <Routes>
                <Route
                  path="/*"
                  element={
                    <HomeContainer
                      authTokenSubject={authTokenSubject}
                      authTokenIsPractice={authTokenIsPractice}
                      authTokenIsAssignment={authTokenIsAssignment}
                    />
                  }
                />
                <Route path="liftoff" element={<StudentHomeContainer />} />
                <Route path="mypathassessmentk5" element={<StudentHomeContainer />} />
                <Route path="mypathassessment612" element={<StudentHomeContainer />} />
                <Route path="launch" element={<EdgeLaunch />} />
                <Route path="link" element={<LinkLaunch />} />
                <Route path="/assignments/*" element={<MyAssignmentsContainer />} />
              </Routes>
            </Suspense>
          </OnDemandTutoringProvider>
        </PathwayProvider>
      </IlpProvider>
    </RewardProvider>
  );
}

function IpaRoutes() {
  const { updateTitle } = usePageTitle();
  useEffect(() => {
    if (updateTitle) {
      setFavicons("ipa");
      updateTitle("Imagine+ Assessment");
    }
  }, [updateTitle]);
  return (
    <Suspense fallback={spinner}>
      <Routes>
        <Route path="assessments/*" element={<AssessmentContainer />} />
        <Route path="*" element={<Navigate to="assessments" />} />
      </Routes>
    </Suspense>
  );
}

function RootContainer({
  studentGradeLevel,
  authState,
  authData,
  authTokenSubject,
  authTokenIsPractice,
  authTokenIsAssignment,
  authTokenExpiry
}: IRootContainerProps): React.ReactElement | null {
  const [isSessionIdSet, setIsSessionIdSet] = useState(false);

  useEffect(() => {
    if (authState === "signedIn" && authData.signInUserSession.accessToken.payload.groups[0] !== "Student") {
      sessionStorage.removeItem(sessionIdKey);
      sessionStorage.setItem(logoutMessageKey, "UnathorizedUserLogoff");
    }
  }, [authState, authData]);

  if (authState === "signedIn") {
    return (
      <>
        <EdgeSessionManager
          handleSessionSet={() => setIsSessionIdSet(true)}
          authTokenExpiry={authTokenExpiry as number}
        />
        {isSessionIdSet && (
          <ThemeProvider initialTheme={selectGradeTheme(studentGradeLevel)}>
            <UserProvider>
              {sessionStorage.getItem("target") === "I+A" ? (
                <IpaRoutes />
              ) : (
                <MyPathRoutes
                  authTokenSubject={authTokenSubject}
                  authTokenIsAssignment={authTokenIsAssignment}
                  authTokenIsPractice={authTokenIsPractice}
                />
              )}
            </UserProvider>
          </ThemeProvider>
        )}
      </>
    );
  }
  return null;
}
export default RootContainer;
